import { useState, useEffect } from "react";
import { formatDisplayDate, calculatePitchDueDate ,formatDateAndTime } from "../../utils/dates";
import { Link } from "react-router-dom";
import { Menu } from "@headlessui/react";
import { EllipsisVerticalIcon, TruckIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { pitchesApi } from "../../../repositories/pitches";
import { PencilIcon, EyeIcon, Trash2,User } from "lucide-react";
import {calculatePitchesDraftedDueDate } from "../../utils/dates";
import {
  dataTableRowClass,
  dataTableTdClass,
} from "../../../components/ui/DataTable";
import StatusBadge from "../../../components/ui/StatusBadge";
import {
  rowMenuPanelClass,
  rowMenuTransitionClass,
  rowMenuTriggerClass,
} from "../../../components/ui/RowActionsMenu";
import { cn } from "../../lib/utils";
interface DashboardTableRowProps {
  data: {
    id: number;
    userId: number;
    companyName: string;
    artistName: string;
    releaseTitle: string;
    releaseDate: string;
    configuration: string;
    releaseUPC: string;
    trackISRC: string;
    status: string;
    deliveredDSPs?: string[];
    AmazonInfo?: any;
    SpotifyInfo?: any;
    DeezerInfo?: any;
    TidalInfo?: any;
    AudiomackInfo?: any;
    submissionDate?: any;
    provisionalFields?: string[];
    provisionalNote?: string;
  };
  onStatusChange: () => void;
  checked?: boolean;
  onCheck?: (id: number, checked: boolean) => void;
  onOpenDSPModal?: (pitchId: number, mode: "admin" | "user", deliveredDSPs?: string[]) => void;
  onOpenDeleteModal?: (pitchId: number) => void;
  onSetReminder?: (pitchId: number) => void;
  isFirstRow?: boolean;
}

export default function DashboardTableRow({
  data,
  onStatusChange,
  checked = false,
  onCheck,
  onOpenDSPModal,
  onOpenDeleteModal,
  onSetReminder,
  isFirstRow = false,
}: DashboardTableRowProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [status, setStatus] = useState(data.status || "submitted");
  useEffect(() => {
    try {
      const authData = localStorage.getItem("auth");
      if (authData) {
        const parsedData = JSON.parse(authData);
        setIsAdmin(parsedData.user?.role === "admin");
      }
    } catch (error) {
      console.error("Error reading localStorage:", error);
    }
  }, []);

  useEffect(() => {
    setStatus(data.status || "submitted");
  }, [data.status]);

  const dueDraftStyling = () => {
    if (!isAdmin && status === 'draft') {
      return calculatePitchesDraftedDueDate(data.releaseDate);
    }
    return '';
  };

  const handleMarkAsDelivered = () => {
    onOpenDSPModal?.(data.id, "admin", data.deliveredDSPs);
  };

  const handleUpdateDSPs = () => {
    onOpenDSPModal?.(data.id, "admin", data.deliveredDSPs);
  };

  const handleMarkResolved = async () => {
    try {
      await pitchesApi.resolveProvisional(data.id);
      setStatus("delivered");
      onStatusChange();
    } catch (error) {
      console.error("Failed to resolve provisional pitch:", error);
    }
  };

  const handleUserDeliveredClick = () => {
    const isDeliveredVariant = ["delivered", "delivered - update pending", "delivered - re-delivery needed"].includes(data.status);
    if (isDeliveredVariant && !isAdmin) {
      onOpenDSPModal?.(data.id, "user", data.deliveredDSPs);
    }
  };

  // Badge markup lives in components/ui/StatusBadge so the dashboard and the
  // submission detail view cannot drift apart. Draft rows still link to the
  // editor, which is what the href does.
  const getStatusBadge = (currentStatus: string) => (
    <StatusBadge
      status={currentStatus}
      isAdmin={isAdmin}
      href={currentStatus === "draft" ? `/pitch/edit/${data.id}?from=dashboard` : undefined}
    />
  );

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onCheck) {
      onCheck(data.id, e.target.checked);
    }
  };

  const handleDeleteDraft = () => {
    onOpenDeleteModal?.(data.id);
  };

  // Shared menu-item styling so every action in the dropdown matches.
  const menuItemClass = (active: boolean, tone: "default" | "danger" | "accent" = "default") =>
    `${active ? "bg-primary-50" : ""} group flex w-full items-center px-4 py-2 text-sm transition-colors ${
      tone === "danger"
        ? "text-danger-600"
        : tone === "accent"
          ? "text-primary-700"
          : "text-neutral-700"
    }`;
  const menuIconClass = "flex-shrink-0 w-4 h-4 mr-2.5";
  return (
    <>
      <tr
        className={dataTableRowClass}
      >
        {isAdmin && (
          <td className={dataTableTdClass}>
            <input
              type="checkbox"
              checked={checked}
              onChange={handleCheckboxChange}
              aria-label={`Select pitch ${data.artistName} - ${data.releaseTitle}`}
              className="w-4 h-4 rounded border-neutral-300 text-primary-700 focus:border-primary-700"
            />
          </td>
        )}
        {isAdmin && <td className={dataTableTdClass}>{data.companyName}</td>}
        <td className={`${dataTableTdClass} font-medium ${dueDraftStyling()}`}>{data.artistName}</td>
        <td className={`${dataTableTdClass} ${dueDraftStyling()}`}>{data.releaseTitle}</td>
        <td className={`${dataTableTdClass} text-neutral-600 ${dueDraftStyling()}`}>{formatDisplayDate(data.releaseDate)}</td>
        <td className={`${dataTableTdClass} text-neutral-600 ${dueDraftStyling()}`}>{calculatePitchDueDate(data.releaseDate)}</td>
        {
          isAdmin && <td className={`${dataTableTdClass} text-neutral-600 ${dueDraftStyling()}`}>{formatDateAndTime(data.submissionDate)}</td>
        }

        <td className={dataTableTdClass}>
          {isAdmin ? (
            <div className="flex items-center justify-between w-full">
              {/* Status Badge */}
              <div className="flex-shrink-0">{getStatusBadge(status)}</div>
            </div>
          ) : (
            <div
              onClick={handleUserDeliveredClick}
              className={`${
                ["delivered", "delivered - update pending", "delivered - re-delivery needed"].includes(data.status)
                  ? "cursor-pointer hover:bg-primary-50 rounded-md p-1 transition-colors"
                  : ""
              }`}
            >
              {["delivered", "delivered - update pending", "delivered - re-delivery needed"].includes(data.status) ? (
                <div className="flex items-center space-x-2">
                  {getStatusBadge(data.status)}
                  <span className="text-xs font-medium text-primary-700 hover:text-primary-900">
                    View Details →
                  </span>
                </div>
              ) : (
                getStatusBadge(data.status)
              )}
              {(data.status === "submitted - update pending" || data.status === "delivered - update pending") &&
                (data.provisionalFields?.length ?? 0) > 0 && (
                <p className="mt-1 text-xs text-warning-700">
                  {data.provisionalFields!.length} field{data.provisionalFields!.length !== 1 ? "s" : ""} need updating — click Edit
                </p>
              )}
            </div>
          )}
        </td>
         <td className={dataTableTdClass}>
          <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className={rowMenuTriggerClass}>
                  <EllipsisVerticalIcon className="w-5 h-5" />
                </Menu.Button>
                <Menu.Items
                  modal={false}
                  transition
                  anchor={{ to: "bottom end", gap: 4 }}
                  className={cn(rowMenuPanelClass, rowMenuTransitionClass)}
                >
                    <div className="py-1">
                      {/* Edit Menu Item - Show for draft, Needs Pitch, and submitted */}
                      {(data.status === "draft" ||
                        data.status === "Needs Pitch" ||
                        data.status === "submitted" ||
                        data.status === "submitted - update pending" ||
                        data.status === "delivered - update pending" ||
                        data.status === "delivered - re-delivery needed") && (
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to={`/pitch/edit/${data.id}?from=dashboard`}
                              className={menuItemClass(active)}
                            >
                              <PencilIcon className={`${menuIconClass} text-neutral-500`} />
                              {data.status === "draft" || data.status === "Needs Pitch"
                                ? "Edit Draft"
                                : "Edit"}
                            </Link>
                          )}
                        </Menu.Item>
                      )}

                      {/* Review Menu Item - Show for submitted and other statuses */}
                      {data.status !== "draft" && data.status !== "Needs Pitch" && (
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to={`/pitch/submission/${data.id}?from=dashboard`}
                              className={menuItemClass(active)}
                            >
                                     <EyeIcon className={`${menuIconClass} text-neutral-500`} />
                              Review
                            </Link>
                          )}
                        </Menu.Item>
                      )}

                      {/* View User Menu Item - Show for all pitches */}
                      {isAdmin && (
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to={ `/admin/userdsp/${data.userId}`}
                            className={menuItemClass(active)}
                          >
                            <User className={`${menuIconClass} text-neutral-500`} />
                            View User
                          </Link>
                        )}
                      </Menu.Item>)}
                      {/* DSP Delivery Actions - Admin only */}
                      {isAdmin && (
                        <>
                          {(status === "submitted" || status === "submitted - update pending" || status === "submitted - re-delivery needed" || status === "delivered - update pending" || status === "delivered - re-delivery needed") && (
                            <>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={handleMarkAsDelivered}
                                    className={menuItemClass(active)}
                                  >
                                    <TruckIcon className={`${menuIconClass} text-success-600`} />
                                    <span>Mark as Delivered</span>
                                  </button>
                                )}
                              </Menu.Item>

                            </>
                          )}

                          {status === "delivered" && (
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={handleUpdateDSPs}
                                  className={menuItemClass(active)}
                                >
                                  <PencilIcon className={`${menuIconClass} text-primary-700`} />
                                  <span>Update DSPs</span>
                                </button>
                              )}
                            </Menu.Item>
                          )}

                          {(status === "delivered - update pending" || status === "delivered - re-delivery needed") && (
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={handleMarkResolved}
                                  className={menuItemClass(active)}
                                >
                                  <CheckCircleIcon className={`${menuIconClass} text-success-600`} />
                                  <span>Mark Resolved</span>
                                </button>
                              )}
                            </Menu.Item>
                          )}

                          {onSetReminder && (
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => onSetReminder(data.id)}
                                  className={menuItemClass(active, "accent")}
                                >
                                  <svg className={menuIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                  </svg>
                                  <span>Set Reminder</span>
                                </button>
                              )}
                            </Menu.Item>
                          )}
                        </>
                      )}

                      {/* Delete Menu Item - Admins can delete draft, Needs Pitch, submitted,
                          and submitted - update pending; users can only delete draft and Needs Pitch */}
                      {(data.status === "draft" ||
                        data.status === "Needs Pitch" ||
                        (isAdmin &&
                          (data.status === "submitted" ||
                            data.status === "submitted - update pending"))
                      ) && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleDeleteDraft}
                              className={menuItemClass(active, "danger")}
                            >
                              <Trash2 className={menuIconClass} />
                              {data.status === "draft" ? "Delete Draft" : "Delete"}
                            </button>
                          )}
                        </Menu.Item>
                      )}
                    </div>
                </Menu.Items>
          </Menu>
        </td>
      </tr>
    </>
  );
}
